/**
 * Platform-aware storage adapter for Supabase auth tokens.
 *
 * On native Capacitor targets (iOS / Android) we use `@capacitor/preferences`
 * which writes to the OS secure key-value store and survives app reinstalls
 * within the same iCloud backup group.  On web we fall back to `localStorage`.
 *
 * The adapter is injected into `createClient()` as the `auth.storage` option
 * so that Supabase's GoTrue client serialises/deserialises sessions through
 * it transparently.
 *
 * Usage:
 *   import { supabaseStorage } from '@/lib/platformStorage';
 */

/** Detects whether the app is running inside a Capacitor native shell. */

import { logger } from "@/lib/logger";

function isNative(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof (
      window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor !== "undefined" &&
    (
      window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }
    ).Capacitor?.isNativePlatform?.() === true
  );
}

type PreferencesPlugin = {
  get: (options: { key: string }) => Promise<{ value: string | null }>;
  set: (options: { key: string; value: string }) => Promise<void>;
  remove: (options: { key: string }) => Promise<void>;
};

let preferencesPluginPromise: Promise<PreferencesPlugin | null> | null = null;

function readLocalStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures and keep the app booting.
  }
}

function removeLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore storage failures and keep the app booting.
  }
}

/**
 * Lazy-loaded reference to `@capacitor/preferences`.
 * The dynamic import avoids bundling Capacitor into the web-only build.
 */
async function getPreferencesPlugin(): Promise<PreferencesPlugin | null> {
  if (!preferencesPluginPromise) {
    preferencesPluginPromise = (async () => {
      try {
        const moduleName = "@capacitor/" + "preferences";
        const { Preferences } = await import(/* @vite-ignore */ moduleName);
        return Preferences as PreferencesPlugin;
      } catch (error) {
        logger.warn(
          "Capacitor Preferences unavailable, falling back to localStorage:",
          error
        );
        return null;
      }
    })();
  }

  return preferencesPluginPromise;
}

/**
 * Supabase-compatible storage interface backed by Capacitor Preferences on
 * native and `localStorage` on web.
 *
 * All methods return Promises to accommodate the async Capacitor API; the
 * Supabase GoTrue client accepts both synchronous and Promise-returning
 * storage implementations.
 */
export const supabaseStorage = {
  async getItem(key: string): Promise<string | null> {
    if (isNative()) {
      const Preferences = await getPreferencesPlugin();
      if (Preferences) {
        try {
          const { value } = await Preferences.get({ key });
          return value;
        } catch (error) {
          logger.warn(
            "Capacitor Preferences get failed, using localStorage fallback:",
            error
          );
        }
      }

      return readLocalStorage(key);
    }
    return readLocalStorage(key);
  },

  async setItem(key: string, value: string): Promise<void> {
    if (isNative()) {
      const Preferences = await getPreferencesPlugin();
      if (Preferences) {
        try {
          await Preferences.set({ key, value });
          return;
        } catch (error) {
          logger.warn(
            "Capacitor Preferences set failed, using localStorage fallback:",
            error
          );
        }
      }

      writeLocalStorage(key, value);
      return;
    }
    writeLocalStorage(key, value);
  },

  async removeItem(key: string): Promise<void> {
    if (isNative()) {
      const Preferences = await getPreferencesPlugin();
      if (Preferences) {
        try {
          await Preferences.remove({ key });
          return;
        } catch (error) {
          logger.warn(
            "Capacitor Preferences remove failed, using localStorage fallback:",
            error
          );
        }
      }

      removeLocalStorage(key);
      return;
    }
    removeLocalStorage(key);
  },
};
